import { useCallback, useMemo } from 'react'
import { caseHasSignedIdd } from '@broker-crm-common/ReferClientModal/ReferClientModal.helpers'
import { useCaseContext } from '@broker-crm-contexts'
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { Share2 } from 'react-feather'
import { FormattedMessage } from 'react-intl'
import { useLocation } from 'react-router-dom'

import { useOpen } from '@acre/utils'
import { Maybe, useGetDocumentSummariesQuery } from '@acre/graphql'
import { trackEventFiltered } from '@acre/design-system'

import ReferClientModal from '../../ReferClientModal/ReferClientModal'
import { ReferralButton } from './ReferClient.styles'

const messagePrefix = 'cases.referClient'

type Props = {
  networkName: Maybe<string>
}

const ReferClient = ({ networkName }: Props) => {
  const theme = useTheme()
  const location = useLocation()
  const { trackEvent } = useMatomo()
  const { id: caseId } = useCaseContext()
  const { isVisible, open: openReferClientModal, close: closeReferClientModal } = useOpen()

  const { data, loading: loadingDocumentSummaries } = useGetDocumentSummariesQuery({
    variables: { filter_case_id: caseId!, filter_archived: false },
    skip: !caseId,
  })

  const hasSignedIdd = useMemo(() => caseHasSignedIdd(data?.documentSummaries), [data?.documentSummaries])

  const handleMouseEnterButton = useCallback(() => {
    if (!hasSignedIdd) {
      trackEventFiltered(trackEvent, location, 'handleHover', 'hoverClientProtectionReferralDisabled')
    }
  }, [hasSignedIdd, location, trackEvent])

  return (
    <>
      <Box
        my={1}
        p={2}
        color={theme.colours.new.violet2}
        sx={{
          background: theme.colours.new.violet1,
          whiteSpace: 'normal',
        }}
        borderRadius={1}
      >
        <Stack gap={2}>
          <Typography fontSize={theme.typography.pxToRem(14)} lineHeight={1.6} fontWeight="bold">
            <FormattedMessage id={`${messagePrefix}.title`} />
          </Typography>
          <Typography fontSize={theme.typography.pxToRem(12)} lineHeight={1.5}>
            <FormattedMessage id={`${messagePrefix}.descriptionFirstFragment`} />
            {networkName ? networkName : <FormattedMessage id={`${messagePrefix}.networkNameFallback`} />}
            <FormattedMessage id={`${messagePrefix}.descriptionSecondFragment`} />
          </Typography>
          <Tooltip
            title={<FormattedMessage id={`${messagePrefix}.disabledTooltip`} />}
            placement="bottom"
            disableHoverListener={hasSignedIdd}
            disableFocusListener={hasSignedIdd}
            disableTouchListener={hasSignedIdd}
          >
            <span onMouseEnter={handleMouseEnterButton}>
              <ReferralButton
                onClick={() => {
                  openReferClientModal()
                  trackEventFiltered(trackEvent, location, 'handleClick', 'openClientProtectionReferralModal')
                }}
                disabled={!hasSignedIdd}
                loading={loadingDocumentSummaries}
              >
                <Stack direction={'row'} gap={1} sx={{ alignItems: 'center' }}>
                  <Share2 size={theme.typography.pxToRem(20)} strokeWidth={1.5} />
                  <Typography fontSize={theme.typography.pxToRem(14)} lineHeight={1.5}>
                    <FormattedMessage id={`${messagePrefix}.createReferral`} />
                  </Typography>
                </Stack>
              </ReferralButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
      <ReferClientModal isVisible={isVisible} onClose={closeReferClientModal} />
    </>
  )
}

export default ReferClient
